import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  Input,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Modal,
  ModalDialog,
  ModalClose,
  FormControl,
  FormLabel,
  AspectRatio,
  Divider,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Select,
  Option
} from '@mui/joy';
import {
  MagnifyingGlass as SearchIcon,
  Check as CheckIcon,
  X as CloseIcon,
  CloudArrowUp as CloudUploadIcon,
  Funnel as FilterIcon,
  Images as CollectionsIcon,
  Buildings as BusinessIcon,
  Sparkle as AIIcon,
  Briefcase as JobIcon,
  Calendar as DateIcon,
  SelectionAll as SelectAllIcon,
  SelectionSlash as DeselectIcon,
  TrendUp as PopularIcon
} from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

interface MediaAsset {
  id: number;
  file: string;
  file_url: string;
  alt_text: string;
  metadata: {
    width?: number;
    height?: number;
    format?: string;
    size?: number;
  };
  source: 'upload' | 'production';
  ai_description?: string;
  created_at: string;
}

interface MarketingPhoto {
  id: number;
  s3_url: string;
  thumbnail_url: string;
  title: string;
  job_name: string;
  job_date?: string;
  job_type?: string;
  taken_at: string;
  metadata: any;
  tags?: string[];
  usage_count?: number;
}

interface MediaPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (media: { url: string; alt?: string; productionPhotoId?: number }) => void;
  siteId: number;
  sectionType?: string;
}

export const MediaPicker: React.FC<MediaPickerProps> = ({ 
  open, 
  onClose, 
  onSelect, 
  siteId,
  sectionType 
}) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSource, setFilterSource] = useState<'all' | 'upload' | 'production'>('all');
  const [selectedMedia, setSelectedMedia] = useState<MediaAsset | MarketingPhoto | null>(null);
  const [altText, setAltText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState<(number | string)[]>([]);
  const [bulkMode, setBulkMode] = useState(false);
  const [generatingAI, setGeneratingAI] = useState<number | string | null>(null);
  const [sortBy, setSortBy] = useState<'recent' | 'popular' | 'relevant'>('recent');
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);

  // Fetch website images
  const { data: mediaAssets, isLoading: assetsLoading, refetch: refetchAssets } = useQuery({
    queryKey: ['media', siteId, searchQuery, filterSource],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('site', siteId.toString());
      if (searchQuery) params.append('search', searchQuery);
      if (filterSource !== 'all') params.append('source', filterSource);
      
      const response = await api.get(`/api/cms/media/?${params.toString()}`);
      const data = response.data || response;
      return Array.isArray(data) ? data : ((data as any).results || []);
    }
  });

  // Fetch marketing photos from combined endpoint (same as Media Library)
  const { data: marketingPhotos, isLoading: photosLoading } = useQuery({
    queryKey: ['marketing-photos', searchQuery],
    queryFn: async () => {
      const params = searchQuery ? `?search=${searchQuery}` : '';
      const response = await api.get(`/api/marketing/combined-media/${params}`);
      const data = response.data || response;
      const results = Array.isArray(data) ? data : ((data as any).results || []);
      // Map combined media format to MediaPicker format
      return results.map((item: any) => {
        // Parse the ID to determine source
        const isProduction = item.id?.toString().startsWith('production_');
        const isMarketing = item.id?.toString().startsWith('marketing_');
        
        return {
          id: item.id, // Keep the prefixed ID as-is
          file_url: item.file || item.thumbnail,
          thumbnail_url: item.thumbnail || item.file,
          s3_url: item.file,
          alt_text: item.alt_text || item.description || '',
          title: item.name || item.description || 'Marketing Photo',
          source: item.source || (isProduction ? 'production' : 'marketing'),
          source_app_display: item.source_app_display || (isProduction ? 'Production' : 'Marketing'),
          job_name: item.job_name,
          job_address: item.job_address,
          tags: item.tags || [],
          created_at: item.uploaded_at || item.created_at,
          file_size: item.file_size
        };
      });
    },
    enabled: activeTab === 1
  });

  // Fetch smart suggestions with enhanced relevance
  const { data: suggestions } = useQuery({
    queryKey: ['media-suggestions', sectionType, sortBy, siteId],
    queryFn: async () => {
      if (!sectionType || !siteId) return null;
      const params = new URLSearchParams();
      params.append('sort', sortBy);
      params.append('site', siteId.toString());
      const response = await api.get(`/api/cms/media/suggestions/${sectionType}/?${params.toString()}`);
      return response;
    },
    enabled: !!sectionType && !!siteId && activeTab === 0
  });

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('site', siteId.toString());
      formData.append('alt_text', altText || file.name);

      // Simple upload without progress tracking for now
      const response = await api.post('/api/cms/media/upload/', formData);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media', siteId] });
      setUploading(false);
      setUploadProgress(0);
      setAltText('');
      refetchAssets();
      // Switch to Website Images tab and show success message
      setActiveTab(0);
      setUploadSuccess('Image uploaded successfully!');
      // Clear success message after 5 seconds
      setTimeout(() => setUploadSuccess(null), 5000);
    },
    onError: (error: any) => {
      console.error('Upload error:', error);
      setUploading(false);
      setUploadProgress(0);
    }
  });

  // Import from production mutation
  const importMutation = useMutation({
    mutationFn: async (photoIds: (number | string)[]) => {
      // Separate production photos and marketing media
      const productionPhotoIds: number[] = [];
      const marketingMediaIds: number[] = [];
      
      photoIds.forEach(id => {
        if (typeof id === 'string') {
          if (id.startsWith('production_')) {
            productionPhotoIds.push(parseInt(id.replace('production_', '')));
          } else if (id.startsWith('marketing_')) {
            marketingMediaIds.push(parseInt(id.replace('marketing_', '')));
          }
        } else if (typeof id === 'number') {
          productionPhotoIds.push(id);
        }
      });
      
      // For now, only import production photos
      // Marketing media are already in the CMS system
      if (productionPhotoIds.length === 0) {
        // No production photos to import
        return { imported: [] };
      }
      
      const response = await api.post('/api/cms/media/import-from-production/', {
        site: siteId,
        photo_ids: productionPhotoIds
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media', siteId] });
      setSelectedPhotos([]);
      setActiveTab(0); // Switch back to website images
      refetchAssets();
    }
  });

  // Auto-import latest marketing photos
  const autoImportMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/api/cms/media/import-latest/', {
        site: siteId
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media', siteId] });
      refetchAssets();
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (mediaId: number | string) => {
      // Can only delete CMS media (numbers)
      if (typeof mediaId === 'number') {
        await api.delete(`/api/cms/media/${mediaId}/`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media', siteId] });
      refetchAssets();
    }
  });

  // Generate AI description mutation
  const generateAIMutation = useMutation({
    mutationFn: async (mediaId: number) => {
      const response = await api.post('/api/cms/media/generate-ai-description/', {
        media_id: mediaId
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media', siteId] });
      setGeneratingAI(null);
      refetchAssets();
    },
    onError: () => {
      setGeneratingAI(null);
    }
  });

  // Bulk alt text update mutation
  const bulkAltTextMutation = useMutation({
    mutationFn: async ({ mediaIds, altText }: { mediaIds: (number | string)[], altText: string }) => {
      // Filter only CMS media (numbers) for bulk update
      const cmsMediaIds = mediaIds.filter(id => typeof id === 'number') as number[];
      if (cmsMediaIds.length === 0) return;
      
      const response = await api.post('/api/cms/media/bulk-update-alt/', {
        media_ids: cmsMediaIds,
        alt_text: altText
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media', siteId] });
      setSelectedPhotos([]);
      setBulkMode(false);
      refetchAssets();
    }
  });

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    setUploading(true);
    uploadMutation.mutate(file);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleSelectMedia = (media: MediaAsset | MarketingPhoto) => {
    setSelectedMedia(media);
    if ('alt_text' in media) {
      setAltText(media.alt_text || (media.ai_description as string) || '');
    }
  };

  const handleInsert = () => {
    if (!selectedMedia) return;

    let url = '';
    if ('file_url' in selectedMedia) {
      url = selectedMedia.file_url;
    } else if ('s3_url' in selectedMedia) {
      url = selectedMedia.s3_url;
    }

    onSelect({
      url,
      alt:
        altText ||
        ('alt_text' in selectedMedia
          ? selectedMedia.alt_text || selectedMedia.ai_description || ''
          : '')
    });
    onClose();
  };

  const handleImportSelected = () => {
    if (selectedPhotos.length > 0) {
      // If no siteId (during onboarding), just return the selected photos
      if (!siteId || siteId === 0) {
        // Get the selected marketing photos and pass them back via onSelect
        const selected = marketingPhotos?.filter((photo: any) => selectedPhotos.includes(photo.id));
        if (selected && selected.length > 0 && onSelect) {
          selected.forEach((photo: any) => {
            // Extract numeric ID if it's a production photo
            let productionPhotoId: number | undefined;
            if (photo.source === 'production' && typeof photo.id === 'string' && photo.id.startsWith('production_')) {
              productionPhotoId = parseInt(photo.id.replace('production_', ''));
            } else if (photo.source === 'production' && typeof photo.id === 'number') {
              productionPhotoId = photo.id;
            }
            
            onSelect({
              url: photo.file_url || photo.s3_url || photo.thumbnail_url || '',
              alt: photo.alt_text || '',
              productionPhotoId
            });
          });
          setSelectedPhotos([]);
          onClose();
        }
      } else {
        // Normal import for existing sites
        importMutation.mutate(selectedPhotos);
      }
    }
  };

  const togglePhotoSelection = (photoId: number | string) => {
    setSelectedPhotos(prev =>
      prev.includes(photoId)
        ? prev.filter(id => id !== photoId)
        : [...prev, photoId]
    );
  };

  const handleSelectAll = () => {
    let items;
    if (activeTab === 0) {
      // Handle paginated response for mediaAssets
      items = Array.isArray(mediaAssets) ? mediaAssets : (mediaAssets?.results || []);
    } else {
      items = marketingPhotos;
    }
    if (items) {
      setSelectedPhotos(items.map((item: any) => item.id));
    }
  };

  const handleDeselectAll = () => {
    setSelectedPhotos([]);
  };

  const handleGenerateAIDescription = async (mediaId: number | string) => {
    if (typeof mediaId === 'string') {
      // Can't generate AI for non-CMS media
      return;
    }
    setGeneratingAI(mediaId);
    generateAIMutation.mutate(mediaId);
  };

  const sortedMediaAssets = React.useMemo(() => {
    if (!mediaAssets) return [] as MediaAsset[];
    // Handle both array and paginated response
    const assets = Array.isArray(mediaAssets) ? mediaAssets : (mediaAssets.results || []);
    return [...assets].sort((a, b) => {
      if (a.source === b.source) {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
      return a.source === 'production' ? -1 : 1;
    });
  }, [mediaAssets]);

  const renderMediaGrid = (items: any[], isMarketingPhotos = false) => {
    if (!items || items.length === 0) {
      return (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography level="body-md" sx={{ color: 'text.secondary' }}>
            {searchQuery ? 'No images found matching your search' : 'No images available'}
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={2}>
        {items.map((item) => {
          const isSelected = selectedMedia?.id === item.id;
          
          return (
            <Grid xs={6} sm={4} md={3} key={item.id}>
              <Card
                variant={isSelected ? 'solid' : 'outlined'}
                color={isSelected ? 'primary' : 'neutral'}
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  position: 'relative',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 'md'
                  },
                  '&:hover .ai-button': {
                    opacity: 1
                  }
                }}
                onClick={() => {
                  if (bulkMode && !isMarketingPhotos) {
                    togglePhotoSelection(item.id);
                  } else if (isMarketingPhotos) {
                    // For marketing photos, directly select for use
                    handleSelectMedia(item);
                  } else {
                    handleSelectMedia(item);
                  }
                }}
              >
                {!isMarketingPhotos && !item.ai_description && (
                  <IconButton
                    className="ai-button"
                    size="sm"
                    variant="solid"
                    color="primary"
                    sx={{
                      position: 'absolute',
                      top: 4,
                      left: 4,
                      zIndex: 1,
                      opacity: 0,
                      transition: 'opacity 0.2s'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleGenerateAIDescription(item.id);
                    }}
                    loading={generatingAI === item.id}
                  >
                    <AIIcon />
                  </IconButton>
                )}
                <AspectRatio ratio="4/3">
                  <img
                    src={item.file_url || item.thumbnail_url || item.s3_url}
                    alt={item.alt_text || item.title || 'Image'}
                    loading="lazy"
                    style={{ objectFit: 'cover' }}
                  />
                  {isSelected && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'var(--brand-text, #7013f5)',
                        borderRadius: '50%',
                        p: 0.5
                      }}
                    >
                      <CheckIcon color="white" size={16} />
                    </Box>
                  )}
                </AspectRatio>
                <Box sx={{ p: 1 }}>
                  <Typography level="body-xs" noWrap>
                    {item.alt_text || item.title || 'Untitled'}
                  </Typography>
                  {item.source && (
                    <Chip size="sm" variant="soft" color={item.source === 'production' ? 'success' : 'neutral'}>
                      {item.source === 'production' ? 'Uploaded from Jobs' : 'Uploaded'}
                    </Chip>
                  )}
                  {item.job_name && (
                    <Box sx={{ mt: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <JobIcon size={12} color="var(--joy-palette-text-secondary)" />
                        <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                          {item.job_name}
                        </Typography>
                      </Box>
                      {item.job_date && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                          <DateIcon size={12} color="var(--joy-palette-text-secondary)" />
                          <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                            {new Date(item.job_date).toLocaleDateString()}
                          </Typography>
                        </Box>
                      )}
                      {item.usage_count && item.usage_count > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                          <PopularIcon size={12} color="var(--joy-palette-warning-500)" />
                          <Typography level="body-xs" sx={{ color: 'warning.700' }}>
                            Used {item.usage_count} time{item.usage_count > 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog size="lg" sx={{ maxWidth: '90vw', maxHeight: '90vh', overflow: 'hidden' }}>
        <ModalClose />
        <Typography level="h4" sx={{ mb: 2 }}>
          Select Image
        </Typography>

        <Box sx={{ height: 'calc(90vh - 200px)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value as number)} sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <TabList>
              <Tab>
                <CollectionsIcon style={{ marginRight: 8 }} />
                Website Images
              </Tab>
              <Tab>
                <BusinessIcon style={{ marginRight: 8 }} />
                Marketing Photos
              </Tab>
              <Tab>
                <CloudUploadIcon style={{ marginRight: 8 }} />
                Upload New
              </Tab>
            </TabList>

            <TabPanel value={0} sx={{ p: 0, flex: 1, overflow: 'auto' }}>
              {uploadSuccess && (
                <Alert
                  color="success"
                  sx={{ mb: 2 }}
                  endDecorator={
                    <IconButton
                      size="sm"
                      color="success"
                      onClick={() => setUploadSuccess(null)}
                    >
                      <CloseIcon />
                    </IconButton>
                  }
                >
                  {uploadSuccess}
                </Alert>
              )}
              <Box sx={{ mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid xs={12} sm={4}>
                    <Input
                      startDecorator={<SearchIcon />}
                      placeholder="Search images..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </Grid>
                  <Grid xs={6} sm={2}>
                    <Select
                      value={filterSource}
                      onChange={(_, value) => setFilterSource(value as any)}
                      startDecorator={<FilterIcon />}
                      size="sm"
                    >
                      <Option value="all">All Sources</Option>
                      <Option value="upload">Uploaded</Option>
                      <Option value="production">Uploaded from Jobs</Option>
                    </Select>
                  </Grid>
                  <Grid xs={6} sm={2}>
                    <Select
                      value={sortBy}
                      onChange={(_, value) => setSortBy(value as any)}
                      size="sm"
                    >
                      <Option value="recent">Most Recent</Option>
                      <Option value="popular">Most Used</Option>
                      <Option value="relevant">Most Relevant</Option>
                    </Select>
                  </Grid>
                  <Grid xs={12} sm={4} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <Button
                      size="sm"
                      variant="soft"
                      onClick={() => autoImportMutation.mutate()}
                      loading={autoImportMutation.isPending}
                    >
                      Auto-import Marketing Photos
                    </Button>
                    <Button
                      size="sm"
                      variant={bulkMode ? 'solid' : 'outlined'}
                      color={bulkMode ? 'primary' : 'neutral'}
                      onClick={() => setBulkMode(!bulkMode)}
                    >
                      Bulk Edit
                    </Button>
                    {bulkMode && (
                      <>
                        <IconButton
                          size="sm"
                          onClick={handleSelectAll}
                          title="Select All"
                        >
                          <SelectAllIcon />
                        </IconButton>
                        <IconButton
                          size="sm"
                          onClick={handleDeselectAll}
                          title="Deselect All"
                        >
                          <DeselectIcon />
                        </IconButton>
                      </>
                    )}
                  </Grid>
                </Grid>

                {suggestions && Array.isArray(suggestions) && suggestions.length > 0 && (
                  <Alert
                    color="primary"
                    variant="soft"
                    sx={{ mt: 2 }}
                    startDecorator={<AIIcon />}
                  >
                    <Box>
                      <Typography level="body-sm" fontWeight="md" sx={{ mb: 1 }}>
                        AI-Recommended for {sectionType}:
                      </Typography>
                      <Grid container spacing={1}>
                        {(suggestions as any[]).slice(0, 8).map((suggestion: MediaAsset & { relevance_score?: number }) => (
                          <Grid xs={6} sm={3} key={suggestion.id}>
                            <Card
                              variant="soft"
                              sx={{
                                cursor: 'pointer',
                                '&:hover': { bgcolor: 'primary.100' }
                              }}
                              onClick={() => handleSelectMedia(suggestion)}
                            >
                              <AspectRatio ratio="16/9">
                                <img
                                  src={suggestion.file_url || (suggestion as any).thumbnail_url}
                                  alt={suggestion.alt_text || 'Suggested image'}
                                  loading="lazy"
                                />
                              </AspectRatio>
                              <Typography level="body-xs" sx={{ mt: 0.5, px: 0.5 }} noWrap>
                                {suggestion.alt_text || 'Suggested'}
                                {suggestion.relevance_score && (
                                  <Chip size="sm" variant="soft" color="success" sx={{ ml: 0.5 }}>
                                    {Math.round(suggestion.relevance_score * 100)}% match
                                  </Chip>
                                )}
                              </Typography>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Alert>
                )}

                {bulkMode && selectedPhotos.length > 0 && (
                  <Alert
                    color="warning"
                    sx={{ mt: 2 }}
                    endDecorator={
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="sm"
                          variant="soft"
                          onClick={() => {
                            const newAltText = prompt('Enter alt text for selected images:');
                            if (newAltText) {
                              bulkAltTextMutation.mutate({
                                mediaIds: selectedPhotos,
                                altText: newAltText
                              });
                            }
                          }}
                        >
                          Set Alt Text
                        </Button>
                        <Button
                          size="sm"
                          variant="soft"
                          color="danger"
                          onClick={() => {
                            if (confirm(`Delete ${selectedPhotos.length} selected images?`)) {
                              selectedPhotos.forEach(id => deleteMutation.mutate(id));
                              setSelectedPhotos([]);
                            }
                          }}
                        >
                          Delete Selected
                        </Button>
                      </Box>
                    }
                  >
                    {selectedPhotos.length} image{selectedPhotos.length > 1 ? 's' : ''} selected
                  </Alert>
                )}
              </Box>

              {assetsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                renderMediaGrid(sortedMediaAssets)
              )}
            </TabPanel>

            <TabPanel value={1} sx={{ p: 0, flex: 1, overflow: 'auto' }}>
              <Box sx={{ mb: 2 }}>
                <Input
                  startDecorator={<SearchIcon />}
                  placeholder="Search marketing photos..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </Box>

              {photosLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                renderMediaGrid(marketingPhotos || [], false)
              )}
            </TabPanel>

            <TabPanel value={2} sx={{ p: 0 }}>
              <Box
                sx={{
                  border: dragActive ? '2px dashed' : '2px dashed transparent',
                  borderColor: dragActive ? 'primary.500' : 'neutral.300',
                  borderRadius: 'md',
                  p: 4,
                  textAlign: 'center',
                  bgcolor: dragActive ? 'primary.50' : 'background.level1',
                  transition: 'all 0.2s',
                  cursor: 'pointer'
                }}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
                
                <CloudUploadIcon size={48} color="var(--joy-palette-primary-500)" style={{ marginBottom: 16 }} />
                <Typography level="h4" sx={{ mb: 1 }}>
                  {uploading ? 'Uploading...' : 'Drop image here or click to browse'}
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  Supports JPG, PNG, GIF, WebP (max 50MB)
                </Typography>

                {uploading && (
                  <Box sx={{ mt: 3, px: 4 }}>
                    <CircularProgress
                      determinate
                      value={uploadProgress}
                      size="lg"
                      thickness={4}
                    />
                    <Typography level="body-sm" sx={{ mt: 1 }}>
                      {uploadProgress}% uploaded
                    </Typography>
                  </Box>
                )}
              </Box>

              <FormControl sx={{ mt: 3 }}>
                <FormLabel>Alt Text (for accessibility)</FormLabel>
                <Input
                  value={altText}
                  onChange={(e) => setAltText(e.target.value)}
                  placeholder="Describe the image..."
                />
              </FormControl>
            </TabPanel>
          </Tabs>
        </Box>

        <Divider sx={{ my: 2 }} />

        {selectedMedia && (activeTab === 0 || activeTab === 1) && (
          <Box sx={{ mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid xs={12} sm={8}>
                <FormControl>
                  <FormLabel>Alt Text</FormLabel>
                  <Input
                    value={altText}
                    onChange={(e) => setAltText(e.target.value)}
                    placeholder="Describe the image for accessibility..."
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={4}>
                {'metadata' in selectedMedia && selectedMedia.metadata && (
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    {selectedMedia.metadata.width}×{selectedMedia.metadata.height} • 
                    {selectedMedia.metadata.format?.toUpperCase()}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="solid"
            color="primary"
            onClick={handleInsert}
            disabled={!selectedMedia}
          >
            Insert Image
          </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
};